import {ChangeDetectionStrategy, Component, QueryList, TemplateRef, ViewChild, ViewChildren} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {AsyncPipe} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {Observable} from "rxjs";
import {GlobalService} from "@app/_services/global.service";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {EdiSenderReceiversAdminService} from "@app/_services/admin/edi-sender-receivers-admin.service";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {Customer, EdiPartnerMapping, EdiValidSenderReceiver} from "@app/_models/api";
import {isControlInvalid} from "@app/_helpers/form-validation";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

@Component({
    selector: 'app-edi-sender-receivers',
    imports: [
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        SortableDirective,
        ReactiveFormsModule,
        ModalConfirmComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './edi-sender-receivers.component.html'
})
export class EdiSenderReceiversComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('addModal') addModal!: TemplateRef<any>;
    @ViewChild('helpModal') helpModal!: TemplateRef<any>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;

    form = new FormGroup({
        isaSenderId: new FormControl<string>("", {nonNullable: true, validators: Validators.required}),
        isaReceiverId: new FormControl<string>("", {nonNullable: true, validators: Validators.required}),
        isActive: new FormControl<boolean>(true, {nonNullable: true})
    });

    partners: EdiPartnerMapping[];
    customers: Customer[];
    savingData = false;
    total$: Observable<number>;
    combinations$: Observable<EdiValidSenderReceiver[]>;

    private modalRef!: NgbModalRef;

    constructor(
        public globalService: GlobalService,
        public service: EdiSenderReceiversAdminService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private toastService: ToastService,
        private modalService: NgbModal) {

        const resolved = this.route.snapshot.data["resolvedData"];
        this.service.data = resolved.data;
        this.partners = resolved.partners;
        this.customers = [...resolved.customers].sort((a: Customer, b: Customer) => a.name.localeCompare(b.name));
        this.combinations$ = this.service.data$;
        this.total$ = this.service.total$;
        this.service.sortColumn = 'customerName';
        this.service.sortDirection = 'asc';
        this.service.refreshDataSource();
    }

    protected readonly isControlInvalid = isControlInvalid;

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    onAdd() {
        this.form.reset({isaSenderId: "", isaReceiverId: "", isActive: true});
        this.modalRef = this.modalService.open(this.addModal, {
            modalDialogClass: "modal-dialog-centered modal modal-lg",
            backdrop: "static",
        });
    }

    openHelp() {
        this.modalService.open(this.helpModal, {
            modalDialogClass: "modal-dialog-centered modal-lg modal-dialog-scrollable",
        });
    }

    closeModal() {
        this.modalConfirmComponent.confirmIfDirty(this.form, () => this.modalRef.dismiss());
    }

    onSave() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.toastService.showDanger("Invalid Data", "Please select a sender and a receiver.");
            return;
        }

        this.savingData = true;
        this.apiService.createEdiValidSenderReceiver(this.form.getRawValue()).subscribe({
            next: saved => {
                this.savingData = false;
                this.service.data = [...this.service.data, saved];
                this.service.refreshDataSource();
                this.toastService.showSuccess('Combination Added', 'Saved successfully.');
                this.modalRef.close();
            },
            error: err => {
                this.savingData = false;
                this.toastService.showDanger('Error Adding Combination', err?.error?.detail || 'Unable to add combination.');
            }
        });
    }

    onToggleActive(combination: EdiValidSenderReceiver, checkbox: HTMLInputElement) {
        const isActive = !combination.isActive;
        checkbox.checked = combination.isActive;
        const pair = `${combination.senderName ?? combination.isaSenderId} → ${combination.customerName ?? combination.isaReceiverId}`;
        this.modalConfirmComponent.open(isActive
            ? `Activate ${pair}? Files from this sender for this customer, including any held back, will be processed in the next run.`
            : `Deactivate ${pair}? New files from this sender for this customer will not be processed.`);
        this.modalConfirmComponent.modalRef.result.then(() => {
            this.globalService.showWaitOverlay();
            this.apiService.updateEdiValidSenderReceiver(combination.id, isActive).subscribe({
                next: saved => {
                    this.globalService.hideWaitOverlay();
                    this.service.data = this.service.data.map(c => c.id === saved.id ? saved : c);
                    this.service.refreshDataSource();
                    this.toastService.showSuccess(isActive ? 'Combination Activated' : 'Combination Deactivated', 'Saved successfully.');
                },
                error: err => {
                    this.globalService.hideWaitOverlay();
                    this.toastService.showDanger('Error Updating Combination', err?.error?.detail || 'Unable to update combination.');
                }
            });
        }, () => {
        });
    }
}
